export interface PerformanceMetric {
  id: string;
  action: string;
  target?: string;
  startTime: number;
  endTime: number;
  duration: number;
  customAttributes?: Record<string, any>;
}

const MAX_METRICS = 1000;

class PerformanceMetricsServiceImpl {
  private metrics: PerformanceMetric[] = [];

  async recordMetric(metric: PerformanceMetric): Promise<void> {
    this.metrics.push(metric);
    this.pruneOldMetrics();
  }

  async getMetrics(filters?: {
    action?: string;
    startTime?: number;
    endTime?: number;
    limit?: number;
  }): Promise<PerformanceMetric[]> {
    let metrics = [...this.metrics];

    if (filters?.action) {
      metrics = metrics.filter((m) => m.action === filters.action);
    }

    if (filters?.startTime) {
      metrics = metrics.filter((m) => m.startTime >= filters.startTime);
    }

    if (filters?.endTime) {
      metrics = metrics.filter((m) => m.endTime <= filters.endTime);
    }

    metrics.sort((a, b) => b.startTime - a.startTime);

    if (filters?.limit) {
      metrics = metrics.slice(0, filters.limit);
    }

    return metrics;
  }

  async clearMetrics(): Promise<void> {
    this.metrics = [];
  }

  async getMetricsCount(): Promise<number> {
    return this.metrics.length;
  }

  private pruneOldMetrics(): void {
    if (this.metrics.length <= MAX_METRICS) return;

    // Keep the most recent MAX_METRICS entries
    this.metrics.sort((a, b) => b.startTime - a.startTime);
    this.metrics = this.metrics.slice(0, MAX_METRICS);
  }

  async getStatistics(action?: string): Promise<{
    count: number;
    avgDuration: number;
    minDuration: number;
    maxDuration: number;
    totalDuration: number;
  }> {
    const metrics = await this.getMetrics({ action });

    if (metrics.length === 0) {
      return {
        count: 0,
        avgDuration: 0,
        minDuration: 0,
        maxDuration: 0,
        totalDuration: 0,
      };
    }

    const durations = metrics.map((m) => m.duration);
    const totalDuration = durations.reduce((sum, d) => sum + d, 0);
    const avgDuration = totalDuration / durations.length;
    const minDuration = Math.min(...durations);
    const maxDuration = Math.max(...durations);

    return {
      count: metrics.length,
      avgDuration,
      minDuration,
      maxDuration,
      totalDuration,
    };
  }
}

export const PerformanceMetricsService = new PerformanceMetricsServiceImpl();
